import { useMemo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CircleDollarSign, Receipt, Users } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn, formatCurrency } from '@/lib/utils'
import { financeApi } from '@/services/finance'
import { jobsApi } from '@/services/jobs'
import { withServiceInstanceId } from '@/lib/service-instance'
import type { Job } from '@/types'

const toAmount = (value?: number | string | null) => {
  if (value === null || value === undefined || value === '') {
    return 0
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const sumExpenseAmounts = (items: Array<{ amount?: number | string | null }> | undefined) =>
  (items ?? []).reduce((sum, item) => sum + toAmount(item.amount), 0)

type ExpenseLineItem = {
  label: string
  amount: number
  href?: string
}

type PayableStatProps = {
  label: string
  amount: number
  href: string
  icon: ReactNode
}

function PayableStat({ label, amount, href, icon }: PayableStatProps) {
  return (
    <Link
      to={href}
      className="flex min-w-[140px] flex-1 items-center justify-between gap-3 rounded-lg border border-border/70 bg-muted/20 px-3 py-2.5 transition-colors hover:border-primary/40 hover:bg-muted/40"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-muted-foreground">{icon}</span>
        <span className="truncate text-xs text-muted-foreground">{label}</span>
      </div>
      <span className="shrink-0 text-sm font-semibold text-foreground">{formatCurrency(amount)}</span>
    </Link>
  )
}

type JobFinancialSummaryProps = {
  job: Job
  serviceInstanceId?: string | null
  expensesPath: string
  particularsPath: string
}

export function JobFinancialSummary({
  job,
  serviceInstanceId,
  expensesPath,
  particularsPath,
}: JobFinancialSummaryProps) {
  const invoiceQuery = useQuery({
    queryKey: ['invoice-preview', job.id, serviceInstanceId ?? 'none'],
    queryFn: () => financeApi.getInvoicePreview(String(job.id), serviceInstanceId ?? undefined),
    enabled: Boolean(job.id),
  })

  const payrollQuery = useQuery({
    queryKey: ['job-payroll-summary', job.id, serviceInstanceId ?? 'none'],
    queryFn: () => jobsApi.getPayrollSummary(String(job.id), { service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(job.id),
  })

  const particularsQuery = useQuery({
    queryKey: ['job-particulars-totals', job.id, serviceInstanceId ?? 'none'],
    queryFn: () => financeApi.getParticulars({ job_id: String(job.id), page: 1, page_size: 500 }),
    enabled: Boolean(job.id),
  })

  const expensesQuery = useQuery({
    queryKey: ['job-expenses-totals', job.id, serviceInstanceId ?? 'none'],
    queryFn: () => financeApi.getExpenses({ job_id: String(job.id), page: 1, page_size: 500 }),
    enabled: Boolean(job.id),
  })

  const isLoading =
    invoiceQuery.isLoading
    || payrollQuery.isLoading
    || particularsQuery.isLoading
    || expensesQuery.isLoading

  const invoicesPath = withServiceInstanceId(`/job/${job.id}?tab=invoices`, serviceInstanceId)
  const workerPaymentsPath = withServiceInstanceId('/job/worker/payments', serviceInstanceId)

  const metrics = useMemo(() => {
    const invoice = invoiceQuery.data
    const plannerBudgetCap = toAmount(job.worker_budget_amount)
    const budget = plannerBudgetCap > 0 ? plannerBudgetCap : toAmount(job.budget ?? job.job_pay)
    const jobPay = toAmount(job.job_pay ?? job.budget)
    const workerPay = toAmount(invoice?.worker_pay_total ?? job.worker_pay_total)
    const expensesFromSummary = toAmount(job.job_summary?.total_expenses)
    const expensesFromInvoice = toAmount(invoice?.total_expenses) || sumExpenseAmounts(invoice?.expenses)
    const expensesFromList = sumExpenseAmounts(expensesQuery.data?.results)
    const expenses = expensesFromSummary || expensesFromInvoice || expensesFromList

    const particularItems = particularsQuery.data?.results
    const companyPayableInventoryPurchases = (particularItems ?? [])
      .filter((item) => item.payable_by_client !== true)
      .reduce(
        (sum, item) => sum + toAmount(item.line_total ?? item.total_price ?? item.cost),
        0,
      )
    const inventoryPurchases = particularItems
      ? companyPayableInventoryPurchases
      : toAmount(invoice?.total_particulars)

    const customerPayable = toAmount(job.payment_due ?? invoice?.payment_due)
    const workerPayable = toAmount(payrollQuery.data?.total_payroll ?? workerPay)
    const revenue = jobPay
    const totalSpend = workerPay + expenses + inventoryPurchases
    const hasRevenue = revenue > 0
    const profit = hasRevenue ? revenue - totalSpend : null
    const budgetRemaining = budget - totalSpend
    const hasBudget = budget > 0
    const isOverBudget = hasBudget && totalSpend > budget
    const spendPercent = hasBudget ? Math.min((totalSpend / budget) * 100, 100) : 0

    const expenseItems: ExpenseLineItem[] = [
      { label: 'Worker payout', amount: workerPay, href: workerPaymentsPath },
      { label: 'Recorded expenses', amount: expenses, href: expensesPath },
      { label: 'Inventory purchases (company payable)', amount: inventoryPurchases, href: particularsPath },
    ]

    return {
      budget,
      plannerBudgetCap,
      customerPayable,
      workerPayable,
      totalSpend,
      budgetRemaining,
      hasBudget,
      isOverBudget,
      spendPercent,
      expenseItems,
      hasRevenue,
      profit,
      revenue,
    }
  }, [
    expensesQuery.data?.results,
    expensesPath,
    invoiceQuery.data,
    job,
    particularsPath,
    particularsQuery.data?.results,
    payrollQuery.data?.total_payroll,
    workerPaymentsPath,
  ])

  if (isLoading) {
    return (
      <Card className="border-border/80">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-28 w-full rounded-lg" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-border/80">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-3">
        <CardTitle className="text-base">Financial Overview</CardTitle>
        {metrics.hasBudget && metrics.isOverBudget ? (
          <Badge variant="destructive" className="gap-1">
            <AlertTriangle className="h-3 w-3" />
            Over budget
          </Badge>
        ) : null}
      </CardHeader>

      <CardContent className="space-y-3">
        <div
          className={cn(
            'flex items-center justify-between gap-4 rounded-lg border px-3 py-2 text-sm',
            metrics.isOverBudget ? 'border-destructive/30 bg-destructive/5' : 'border-border/70 bg-muted/20',
          )}
        >
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-muted-foreground">{metrics.plannerBudgetCap > 0 ? 'Planner budget cap' : 'Budget'}</span>
              <span className="font-semibold text-foreground">
                {metrics.hasBudget ? formatCurrency(metrics.budget) : '—'}
              </span>
            </div>
            <div className="hidden h-4 w-px bg-border sm:block" />
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-muted-foreground">Spend</span>
              <span className="font-semibold text-foreground">{formatCurrency(metrics.totalSpend)}</span>
            </div>
            {metrics.hasBudget ? (
              <>
                <div className="hidden h-4 w-px bg-border sm:block" />
                <div className="flex items-baseline gap-2">
                  <span className="text-xs text-muted-foreground">
                    {metrics.isOverBudget ? 'Over' : 'Left'}
                  </span>
                  <span
                    className={cn(
                      'font-semibold',
                      metrics.isOverBudget ? 'text-destructive' : 'text-emerald-700 dark:text-emerald-300',
                    )}
                  >
                    {formatCurrency(Math.abs(metrics.budgetRemaining))}
                  </span>
                </div>
                <div className="hidden min-w-[100px] flex-1 items-center md:flex">
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all',
                        metrics.isOverBudget ? 'bg-destructive' : 'bg-primary',
                      )}
                      style={{ width: `${metrics.spendPercent}%` }}
                    />
                  </div>
                </div>
              </>
            ) : null}
          </div>

          <div className="shrink-0 border-l border-border/70 pl-4 text-right">
            <p className="text-xs text-muted-foreground">Profit</p>
            <p
              className={cn(
                'text-base font-semibold',
                metrics.profit == null
                  ? 'text-muted-foreground'
                  : metrics.profit >= 0
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-destructive',
              )}
            >
              {metrics.profit == null ? '—' : formatCurrency(metrics.profit)}
            </p>
            {!metrics.hasRevenue ? (
              <p className="text-[11px] text-muted-foreground">Job pay not set</p>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row">
          <div className="min-w-0 flex-1 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Expenses</span>
              </div>
              <span className="text-base font-semibold text-foreground">{formatCurrency(metrics.totalSpend)}</span>
            </div>
            <div className="mt-2 divide-y divide-border/60">
              {metrics.expenseItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href!}
                  className="flex items-center justify-between gap-3 py-1.5 text-sm first:pt-0 last:pb-0 transition-colors hover:text-primary"
                >
                  <span className="text-muted-foreground">{item.label}</span>
                  <span className="font-medium text-foreground">{formatCurrency(item.amount)}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-2 lg:w-[340px] lg:gap-2">
            <PayableStat
              label="Customer payable"
              amount={metrics.customerPayable}
              href={invoicesPath}
              icon={<CircleDollarSign className="h-3.5 w-3.5" />}
            />
            <PayableStat
              label="Worker payable"
              amount={metrics.workerPayable}
              href={workerPaymentsPath}
              icon={<Users className="h-3.5 w-3.5" />}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
